import React from 'react';
import { FileText, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../../components/ui/dialog';

export const DocumentViewerDialog = ({ viewingDoc, onClose, onAcknowledge, acknowledging, canAcknowledge = true }) => (
  <Dialog open={!!viewingDoc} onOpenChange={(open) => !open && onClose()}>
    <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-amber-500" />
          {viewingDoc?.title}
        </DialogTitle>
        <DialogDescription className="sr-only"> dialog. Use the form below to continue.</DialogDescription>
      </DialogHeader>
      {viewingDoc?.is_addendum && viewingDoc?.addendum_summary && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>Changes Summary:</strong> {viewingDoc.addendum_summary}
          </p>
        </div>
      )}
      <div className="border-t pt-4">
        <div
          className="prose prose-sm max-w-none"
          dangerouslySetInnerHTML={{ __html: viewingDoc?.content || '' }}
        />
      </div>
      {viewingDoc?.is_expired ? (
        <div className="mt-4 space-y-3">
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-600 shrink-0" />
            <div className="text-sm text-red-800">
              <p>This document expired on <strong>{viewingDoc.expires_at && new Date(viewingDoc.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</strong>.</p>
              <p className="mt-1 text-xs">Previously acknowledged by {viewingDoc.acknowledged_by_name}. A renewal will be issued by the admin.</p>
            </div>
          </div>
        </div>
      ) : viewingDoc?.status === 'acknowledged' ? (
        <div className="mt-4 space-y-2">
          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-green-600 shrink-0" />
            <p className="text-sm text-green-800">
              Acknowledged by <strong>{viewingDoc.acknowledged_by_name}</strong> on{' '}
              {viewingDoc.acknowledged_at && new Date(viewingDoc.acknowledged_at).toLocaleDateString('en-US', {
                month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit'
              })}
            </p>
          </div>
          {viewingDoc.expires_at && (
            <div className="p-2 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2">
              <Clock className="h-3.5 w-3.5 text-blue-500 shrink-0" />
              <p className="text-xs text-blue-700">
                Expires on {new Date(viewingDoc.expires_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          )}
        </div>
      ) : viewingDoc ? (
        <div className="mt-4 pt-4 border-t space-y-3">
          {canAcknowledge ? (
            <>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  By clicking "I Acknowledge", you confirm that you have read and agree to the terms outlined in this document.
                </p>
              </div>
              <div className="flex justify-end">
                <Button
                  className="bg-amber-500 hover:bg-amber-600 text-white"
                  onClick={() => onAcknowledge(viewingDoc.id)}
                  disabled={acknowledging}
                  data-testid="acknowledge-doc-btn"
                >
                  {acknowledging ? 'Processing...' : 'I Acknowledge'}
                </Button>
              </div>
            </>
          ) : (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="no-acknowledge-permission-notice">
              <p className="text-sm text-amber-800">
                Only the account owner (or a team member with profile permissions) can acknowledge this document.
              </p>
            </div>
          )}
        </div>
      ) : null}
    </DialogContent>
  </Dialog>
);
